import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Gallery.css';

// ── LOCAL IMAGE IMPORTS ──────────────────────────────────────────
import img1_1 from '../assets/Gallary/1.1.jpg';
import img1_2 from '../assets/Gallary/1.2.avif';
import img1_3 from '../assets/Gallary/1.3.jpg';
import img2_1 from '../assets/Gallary/2.1.jpg';
import img2_2 from '../assets/Gallary/2.2.webp';
import img2_3 from '../assets/Gallary/2.3.jpg';
import img3_1 from '../assets/Gallary/3.1.jpg';
import img3_2 from '../assets/Gallary/3.2.webp';
import img3_3 from '../assets/Gallary/3.3.webp';
import img4_1 from '../assets/Gallary/4.1.jpg';
import img4_2 from '../assets/Gallary/4.2.webp';
import img4_3 from '../assets/Gallary/4.3.webp';

// ── LOCAL VIDEO IMPORTS ──────────────────────────────────────────
import vid1 from '../assets/Gallary/video1.mp4';
import vid2 from '../assets/Gallary/video2.mp4';
import vid3 from '../assets/Gallary/video3.mp4';
import vid4 from '../assets/Gallary/video4.mp4';
import vid5 from '../assets/Gallary/5.mp4';
import vid6 from '../assets/Gallary/6.mp4';

// ── DATA ─────────────────────────────────────────────────────────
const categories = ['All', 'Residential', 'Commercial', 'Interior', 'Designs', 'Sites'];

const galleryItems = [
  { id: 1,  category: 'Residential', title: 'Residential Project 1', location: 'Pune',       img: img1_1 },
  { id: 2,  category: 'Residential', title: 'Residential Project 2', location: 'Mumbai',     img: img1_2 },
  { id: 3,  category: 'Residential', title: 'Residential Project 3', location: 'Nashik',     img: img1_3 },
  { id: 4,  category: 'Commercial',  title: 'Commercial Project 1',  location: 'Mumbai',     img: img2_1 },
  { id: 5,  category: 'Commercial',  title: 'Commercial Project 2',  location: 'Pune',       img: img2_2 },
  { id: 6,  category: 'Commercial',  title: 'Commercial Project 3',  location: 'Aurangabad', img: img2_3 },
  { id: 7,  category: 'Interior',    title: 'Interior Project 1',    location: 'Pune',       img: img3_1 },
  { id: 8,  category: 'Interior',    title: 'Interior Project 2',    location: 'Mumbai',     img: img3_2 },
  { id: 9,  category: 'Interior',    title: 'Interior Project 3',    location: 'Nashik',     img: img3_3 },
  { id: 10, category: 'Designs',     title: 'Design Project 1',      location: 'Pune',       img: img4_1 },
  { id: 11, category: 'Designs',     title: 'Design Project 2',      location: 'Mumbai',     img: img4_2 },
  { id: 12, category: 'Designs',     title: 'Design Project 3',      location: 'Nagpur',     img: img4_3 },
];

// ── SITE VIDEOS DATA ──────────────────────────────────────────────
const siteVideos = [
  { id: 1, title: 'Residential Site Visit',   location: 'Pune',       category: 'Residential', tag: 'Under Construction', src: vid1 },
  { id: 2, title: 'Villa Foundation Work',    location: 'Mumbai',     category: 'Residential', tag: 'Foundation Stage',   src: vid2 },
  { id: 3, title: 'Commercial Site Progress', location: 'Aurangabad', category: 'Commercial',  tag: 'Structural Work',    src: vid3 },
  { id: 4, title: 'Office Building Site',     location: 'Nashik',     category: 'Commercial',  tag: 'Under Construction', src: vid4 },
  { id: 5, title: 'Interior Work in Progress',location: 'Pune',       category: 'Interior',    tag: 'Finishing Stage',    src: vid5 },
  { id: 6, title: 'Site Completion Walkthrough',location: 'Mumbai',   category: 'Residential', tag: 'Completed',          src: vid6 },
];

// tag color mapping
const tagColors = {
  'Under Construction': { bg: '#fff4e0', color: '#a07c28', dot: '#c49e3c' },
  'Foundation Stage':   { bg: '#e8f0ff', color: '#1a3a6e', dot: '#3a6aee' },
  'Structural Work':    { bg: '#ffeaea', color: '#a02828', dot: '#ee3a3a' },
  'Finishing Stage':    { bg: '#e8f8e8', color: '#287a28', dot: '#3aaa3a' },
  'Completed':          { bg: '#e8f8f0', color: '#1a6e4a', dot: '#2ecc71' },
};

// ── SITE VIDEO CARD ───────────────────────────────────────────────
const SiteVideoCard = ({ item, index }) => {
  const [playing, setPlaying] = useState(false);
  const tag = tagColors[item.tag] || tagColors['Under Construction'];

  return (
    <motion.div
      className="site-card"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
    >
      {/* Video */}
      <div className="site-video-wrap">
        <video
          className="site-video"
          controls
          playsInline
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        >
          <source src={item.src} type="video/mp4" />
        </video>

        {/* Status tag */}
        <div
          className="site-tag"
          style={{ background: tag.bg, color: tag.color }}
        >
          <span className="site-tag-dot" style={{ background: tag.dot }} />
          {item.tag}
        </div>
      </div>

      {/* Info */}
      <div className="site-card-info">
        <div className="site-card-top">
          <span className="site-card-category">{item.category}</span>
          <span className="site-card-id">#{String(item.id).padStart(2, '0')}</span>
        </div>
        <h3 className="site-card-title">{item.title}</h3>
        <div className="site-card-bottom">
          <span className="site-card-location">📍 {item.location}</span>
        </div>
      </div>
    </motion.div>
  );
};

// ── BEFORE & AFTER VIDEO DATA ─────────────────────────────────────
const beforeAfterVideos = [
  { id: 1, title: 'Villa Renovation',        location: 'Pune',   category: 'Residential', duration: '6 months', beforeVideo: vid1, afterVideo: vid2 },
  { id: 2, title: 'Office Transformation',   location: 'Mumbai', category: 'Commercial',  duration: '3 months', beforeVideo: vid3, afterVideo: vid4 },
  { id: 3, title: 'Interior Makeover',       location: 'Nashik', category: 'Interior',    duration: '2 months', beforeVideo: vid5, afterVideo: vid6 },
];

// ── BEFORE/AFTER VIDEO CARD ───────────────────────────────────────
const VideoCard = ({ item }) => {
  const [activeTab, setActiveTab] = useState('before');
  const currentVideo = activeTab === 'before' ? item.beforeVideo : item.afterVideo;

  return (
    <motion.div
      className="ba-card"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
    >
      <div className="ba-tabs">
        <button className={`ba-tab ${activeTab === 'before' ? 'ba-tab-active ba-tab-before' : ''}`} onClick={() => setActiveTab('before')}>⏮ Before</button>
        <div className="ba-tab-divider" />
        <button className={`ba-tab ${activeTab === 'after'  ? 'ba-tab-active ba-tab-after'  : ''}`} onClick={() => setActiveTab('after')}>After ⏭</button>
      </div>
      <div className="ba-video-wrap">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentVideo}
            className="ba-video-inner"
            initial={{ opacity: 0, x: activeTab === 'after' ? 40 : -40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28 }}
          >
            <video controls className="ba-video" playsInline key={currentVideo}>
              <source src={currentVideo} type="video/mp4" />
            </video>
          </motion.div>
        </AnimatePresence>
        <div className={`ba-label ${activeTab === 'before' ? 'ba-label-before' : 'ba-label-after'}`}>
          {activeTab === 'before' ? '⏮ BEFORE' : 'AFTER ⏭'}
        </div>
      </div>
      <div className="ba-info">
        <div className="ba-info-left">
          <span className="ba-category">{item.category}</span>
          <h3 className="ba-title">{item.title}</h3>
          <span className="ba-location">📍 {item.location}</span>
        </div>
        <div className="ba-info-right">
          <span className="ba-duration-label">Duration</span>
          <span className="ba-duration">⏱ {item.duration}</span>
        </div>
      </div>
    </motion.div>
  );
};

// ── MAIN GALLERY ──────────────────────────────────────────────────
const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightbox, setLightbox]             = useState(null);

  const filtered = activeCategory === 'Sites'
    ? []
    : activeCategory === 'All'
      ? galleryItems
      : galleryItems.filter(i => i.category === activeCategory);

  const openLightbox     = (item) => setLightbox(item);
  const closeLightbox    = ()     => setLightbox(null);
  const navigateLightbox = (dir)  => {
    const idx     = filtered.findIndex(i => i.id === lightbox.id);
    const nextIdx = (idx + dir + filtered.length) % filtered.length;
    setLightbox(filtered[nextIdx]);
  };
  const handleBackdropClick = (e) => { if (e.target === e.currentTarget) closeLightbox(); };

  return (
    <div className="gallery-page">

      {/* ── HERO ── */}
      <div className="gallery-hero">
        <p className="gallery-hero-tag">OUR WORK</p>
        <h1>Project Gallery</h1>
        <p className="gallery-hero-sub">
          A showcase of our finest construction and design projects across Maharashtra.
        </p>
      </div>



          {/* ── FILTER TABS ── */}
          <div className="gallery-filters">
            {categories.map(cat => (
              <button
                key={cat}
                className={`filter-btn ${activeCategory === cat ? 'filter-btn-active' : ''} ${cat === 'Sites' ? 'filter-btn-sites' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat === 'Sites' ? '🎥 Sites' : cat}
              </button>
            ))}
          </div>

          {/* ── SITES VIDEO GRID ── */}
          {activeCategory === 'Sites' ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
              <div className="sites-section-header">
                <div className="sites-header-badge">🎥 LIVE SITE FOOTAGE</div>
                <h2>Site Videos</h2>
                <p>Real on-site footage capturing every stage of our construction projects.</p>
              </div>



              <div className="site-grid">
                {siteVideos.map((item, index) => (
                  <SiteVideoCard key={item.id} item={item} index={index} />
                ))}
              </div>
            </motion.div>
          ) : (
            /* ── PHOTO GRID ── */
            <div className="gallery-grid">
              <AnimatePresence mode="wait">
                {filtered.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="gallery-card"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.35, delay: index * 0.06 }}
                    onClick={() => openLightbox(item)}
                  >
                    <div className="gallery-card-img-wrap">
                      <img src={item.img} alt={item.title} loading="lazy" />
                      <div className="gallery-card-overlay">
                        <span className="gallery-card-zoom">⊕</span>
                      </div>
                    </div>
                    <div className="gallery-card-info">
                      <span className="gallery-card-category">{item.category}</span>
                      <h3 className="gallery-card-title">{item.title}</h3>
                      <span className="gallery-card-location">📍 {item.location}</span>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}


      {/* ── LIGHTBOX ── */}
      <AnimatePresence>
        {lightbox && (
          <motion.div className="lightbox-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={handleBackdropClick}>
            <motion.div className="lightbox-box" initial={{ scale: 0.88, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.88, opacity: 0 }} transition={{ duration: 0.25 }}>
              <button className="lb-close" onClick={closeLightbox}>✕</button>
              <button className="lb-arrow lb-prev" onClick={() => navigateLightbox(-1)}>‹</button>
              <img src={lightbox.img} alt={lightbox.title} className="lb-img" />
              <button className="lb-arrow lb-next" onClick={() => navigateLightbox(1)}>›</button>
              <div className="lb-caption">
                <span className="lb-cat">{lightbox.category}</span>
                <h3>{lightbox.title}</h3>
                <span className="lb-loc">📍 {lightbox.location}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Gallery;